// AI Text Generation Service
// Servizio per l'integrazione con LiteLLM tramite Supabase Edge Function

import { aiChatFetch } from '../ai/aiChatFetch';

/**
 * Servizio per la generazione testo AI (descrizioni offerte)
 * Utilizza una Supabase Edge Function come proxy per evitare problemi CORS
 */

interface ChatGptResponse {
  text: string;
  error?: string;
}

const OFFER_SYSTEM_PROMPT = 'Sei un assistente specializzato nella redazione di introduzioni efficaci per offerte commerciali. ' +
  'Produci contenuti professionali, concisi e persuasivi in formato HTML valido. ' +
  'NON strutturare il testo come una email o una lettera. ' +
  'Utilizza elementi HTML come <p>, <ul>, <li>, <strong>, <em> per formattare il contenuto. ' +
  'Crea introduzioni brevi (massimo 3-4 paragrafi) che catturino l\'attenzione e motivino il lettore. ' +
  'Possibili elementi da includere: ' +
  '- Una sintesi del problema/esigenza del cliente ' +
  '- Il valore principale della proposta ' +
  '- Un accenno ai principali benefici ' +
  '- Un elemento distintivo della soluzione ' +
  'IMPORTANTE: NON menzionare mai prezzi, costi o tariffe nella descrizione. I prezzi sono gestiti separatamente. ' +
  'Evita dettagli eccessivi e lunghi elenchi, concentrati su un\'introduzione efficace. ' +
  'Evita formule di cortesia come saluti iniziali o finali. ' +
  'NON includere delimitatori di codice nel tuo output. ' +
  'Rispondi SOLO con il contenuto HTML pulito, pronto da inserire nell\'introduzione dell\'offerta.';

/**
 * Genera testo tramite LiteLLM basato sul prompt fornito
 * @param prompt Il prompt da inviare all'AI
 * @param originalText Il testo originale in HTML (opzionale)
 * @param serviceTitle Il titolo del servizio (opzionale)
 * @returns Promise con il testo generato in HTML o un errore
 */
export async function generateTextWithChatGpt(
  prompt: string,
  originalText?: string,
  serviceTitle?: string
): Promise<ChatGptResponse> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Supabase configuration missing');
      return { text: '', error: 'Configurazione Supabase mancante' };
    }

    // Costruisci il prompt utente per la generazione descrizione offerta
    let userPrompt: string;
    if (originalText) {
      userPrompt = `Servizio: "${serviceTitle || 'Servizio'}".\n\nEcco la descrizione attuale in HTML:\n---\n${originalText}\n---\n\nISTRUZIONI: ${prompt}\n\nApplica esattamente le istruzioni fornite sopra e migliora il testo esistente seguendole. Rispondi SOLO con il contenuto HTML pronto da inserire, senza commenti o altro testo. NON racchiudere la risposta in blocchi di codice. RICORDA: non includere mai prezzi, costi o tariffe nella descrizione.`;
    } else {
      userPrompt = `Servizio: "${serviceTitle || 'Servizio'}".\n\nISTRUZIONI: ${prompt}\n\nApplica esattamente le istruzioni fornite sopra per creare una descrizione per questo servizio in HTML. Segui alla lettera il prompt dell'utente. Usa paragrafi, elenchi puntati e formattazione appropriata dove necessario. Rispondi SOLO con il contenuto HTML pronto da inserire, senza commenti o altro testo. NON racchiudere la risposta in blocchi di codice. IMPORTANTE: non includere mai prezzi, costi o tariffe nella descrizione.`;
    }

    const response = await aiChatFetch(`${supabaseUrl}/functions/v1/ai-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify({
        messages: [
          { role: 'system', content: OFFER_SYSTEM_PROMPT },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0.7,
        max_tokens: 2000
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Errore Edge Function:', errorData);
      return {
        text: '',
        error: `Errore: ${errorData.error || 'Errore sconosciuto'}`
      };
    }

    const data = await response.json();
    return { text: data.content || '' };

  } catch (error) {
    console.error('Errore durante la chiamata AI:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Errore sconosciuto'
    };
  }
}
